import { apiClient } from '@/lib/api-client';
// G2.9: canonical notification enums live in @bbpos/types so the backend
// routing helper and the POS toast router reference the same union.
import type { NotificationType, NotificationPriority } from '@bbpos/types';

// ─── Types ──────────────────────────────────────────────────────────────────

export type { NotificationType, NotificationPriority };

export interface Notification {
  id: string;
  tenant_id: string;
  user_id: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  is_read: boolean;
  action_url: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  /** Only on GET /notifications/:id. */
  read_at?: string | null;
}

export interface NotificationListParams {
  page?: number;
  limit?: number;
  /** One type, or a comma-separated list (the API OR-filters them). */
  type?: string;
  is_read?: string;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface NotificationPreferences {
  email_enabled: boolean;
  sms_enabled: boolean;
  whatsapp_enabled: boolean;
  push_enabled: boolean;
}

// ─── Per-type channel matrix (G2.10) ────────────────────────────────────────
//
// 2-D matrix of (notification_type × channel). Layered on top of the flat
// NotificationPreferences above:
//
//   A channel fires for (user, type) iff
//     NotificationPreferences.<channel>_enabled = true
//   AND
//     (matrix row missing → in-app only) OR (matrix row present AND row.<channel> = true)
//
// in_app is special: always-on at the global layer. The matrix in_app column
// exists for future granularity but the consumer ignores it for now.

export type NotificationChannel = 'in_app' | 'email' | 'sms' | 'whatsapp' | 'push';

export interface NotificationPreferenceMatrixRow {
  notification_type: NotificationType;
  in_app: boolean;
  email: boolean;
  sms: boolean;
  whatsapp: boolean;
  push: boolean;
  updated_at: string;
}

export interface UpdateMatrixCellInput {
  notification_type: NotificationType;
  channel: NotificationChannel;
  enabled: boolean;
}

// ─── Service ────────────────────────────────────────────────────────────────

export const notificationService = {
  listNotifications(params: NotificationListParams = {}) {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        qs.set(key, String(value));
      }
    }
    const query = qs.toString();
    return apiClient<{ data: Notification[]; meta: PaginationMeta }>(
      `/notifications${query ? `?${query}` : ''}`,
    );
  },

  /** One notification for the detail view (T24); 404 when it isn't the caller's to see. */
  getNotification(id: string) {
    return apiClient<Notification>(`/notifications/${id}`);
  },

  getUnreadCount() {
    return apiClient<{ unread_count: number }>('/notifications/unread-count');
  },

  markAsRead(id: string) {
    return apiClient<{ id: string; is_read: boolean; read_at: string }>(
      `/notifications/${id}/read`,
      { method: 'PATCH' },
    );
  },

  markAllAsRead() {
    return apiClient<{ updated_count: number; message: string }>(
      '/notifications/read-all',
      { method: 'PATCH' },
    );
  },

  getPreferences() {
    return apiClient<NotificationPreferences>('/notifications/preferences');
  },

  updatePreferences(prefs: Partial<NotificationPreferences>) {
    return apiClient<NotificationPreferences>('/notifications/preferences', {
      method: 'PATCH',
      body: prefs,
    });
  },

  // ─── G2.10 — Per-type Channel Matrix ─────────────────────────────────────

  /**
   * Fetch the caller's per-type matrix. May return an empty array — the
   * grid renders defaults (in_app=true, others=false) for unset types.
   */
  getMatrix() {
    return apiClient<{ data: NotificationPreferenceMatrixRow[] }>(
      '/notifications/preferences/matrix',
    );
  },

  /**
   * Toggle a single (type, channel) cell. Single-cell upsert so the UI can
   * autosave per click. Returns the updated matrix row.
   */
  updateMatrixCell(input: UpdateMatrixCellInput) {
    return apiClient<NotificationPreferenceMatrixRow>(
      '/notifications/preferences/matrix',
      { method: 'PATCH', body: input },
    );
  },

  /**
   * Wipe every matrix row for the caller. Used by the grid's
   * "Reset to defaults" action — after the DELETE the grid falls back to
   * the column defaults (in_app=true, all expensive channels off).
   * Idempotent.
   */
  resetMatrix() {
    return apiClient<{ removed: number }>(
      '/notifications/preferences/matrix',
      { method: 'DELETE' },
    );
  },

  // ─── G2.12 — Web Push Subscriptions ──────────────────────────────────────

  /**
   * Returns the environment's VAPID public key, or `null` if Web Push is
   * not configured on the server. The frontend uses this key to call
   * `PushManager.subscribe()`.
   */
  getVapidPublicKey() {
    return apiClient<{ public_key: string | null }>(
      '/notifications/push/vapid-public-key',
    );
  },

  /**
   * Persist a browser push subscription on the backend. Idempotent on
   * (user, endpoint) — repeated calls refresh the keys + last_seen_at.
   */
  registerPushSubscription(subscription: PushSubscriptionJSON) {
    return apiClient<{ id: string; endpoint: string; user_agent: string | null }>(
      '/notifications/push/subscriptions',
      { method: 'POST', body: subscription },
    );
  },

  /** Remove a subscription by its endpoint URL. Idempotent. */
  unregisterPushSubscription(endpoint: string) {
    return apiClient<{ removed: number }>(
      '/notifications/push/subscriptions',
      { method: 'DELETE', body: { endpoint } },
    );
  },
};
